import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { getDrawerStyles } from '../../useStyles'
import Typography from '@mui/material/Typography/Typography'
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel'
import Switch from '@mui/material/Switch/Switch'
import Paper from '@mui/material/Paper/Paper'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import { getFormattedAddress } from '@sales-center/common'
import {
  CreateLocationFromAgreementProps,
  CreateServiceLocationFormModel,
} from './utility/types'
import Stack from '@mui/material/Stack'
import { useFormikContext } from 'formik'
import { scrollToLocation } from './utility/functions'
import { useTheme } from '@mui/material/styles'

interface Props {
  validatedLocations: CreateServiceLocationFormModel[]
  title: string
  showOnlyRequired: boolean
  tidalThemeActive?: boolean
  onChangeShowRequired?: (boolean) => void
}

export const CreateLocationHeader: FC<Props> = (props) => {
  const { validatedLocations, title, onChangeShowRequired, showOnlyRequired } =
    props

  const {
    errors,
    values: { locations },
  } = useFormikContext<CreateLocationFromAgreementProps>()

  const onChangeHideOptional = useCallback(
    (_e, checked: boolean) => {
      if (onChangeShowRequired) onChangeShowRequired(checked)
    },
    [onChangeShowRequired],
  )

  const invalidLocations = useMemo(
    () =>
      validatedLocations
        .filter((x) => x.isInvalid)
        .map((l) => ({
          billingLocation: l.billingLocation,
          address: getFormattedAddress(
            l.address1 ?? '',
            l.address2 ?? '',
            l.city ?? '',
            l.state ?? '',
            l.postalCode ?? '',
          ),
          infoLabel:
            l.firstName || l.lastName
              ? `${l.firstName} ${l.lastName}`
              : l.company,
          id: l.id,
        })),
    [validatedLocations],
  )

  const locationsInError = useMemo(() => {
    return errors.locations ? Object.keys(errors.locations).length : 0
  }, [errors])

  const errorBanner = useMemo(() => {
    return invalidLocations?.length === 1
      ? 'Required fields missing'
      : `${locationsInError} Location${
          locationsInError !== 1 ? 's are' : ' is'
        } missing required fields`
  }, [invalidLocations, locationsInError])

  const getlocationInfo = useCallback(
    (id: number) => {
      const l = locations[id]

      const address = getFormattedAddress(
        l.address1 ?? '',
        l.address2 ?? '',
        l.city ?? '',
        l.state ?? '',
        l.postalCode ?? '',
      )
      const name =
        l.firstName || l.lastName ? `${l.firstName} ${l.lastName}` : l.company

      return address ? address : name
    },
    [locations],
  )
  const theme = useTheme()
  const styles = getDrawerStyles(theme, props)
  return (
    <Stack ml={2} mr={2}>
      <Paper elevation={0} style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <Typography variant="h5">{title} </Typography>
        <FormControlLabel
          sx={{
            ...styles.hideRootAdj,
            'MuiLabel-root': styles.hideLabel,
          }}
          labelPlacement="end"
          label={
            <Typography variant="body1" sx={{ fontSize: '16px' }}>
              Show only required fields
            </Typography>
          }
          control={
            <Switch
              size="medium"
              checked={showOnlyRequired}
              onChange={onChangeHideOptional}
            />
          }
        />
      </Paper>
      <Paper
        elevation={1}
        square={false}
        sx={{
          margin: `${theme.spacing(2)} ${theme.spacing(0)}`,
          marginBottom: theme.spacing(0),
          padding: theme.spacing(0),
        }}
      >
        <Box sx={{ paddingBottom: theme.spacing(0.4) }}>
          <Alert
            sx={{
              display: locationsInError === 0 ? 'none' : 'flex',
              alignItems: 'center',
              width: '100%',
            }}
            severity="error"
          >
            <AlertTitle
              sx={{
                fontSize: '16px',
                color: theme.palette.error.dark,
                fontWeight: '600',
                paddingTop: theme.spacing(0.5),
              }}
            >
              {errorBanner}
            </AlertTitle>
          </Alert>

          <Box sx={{ marginTop: theme.spacing(1.2) }}>
            <ul
              style={{
                marginBottom: theme.spacing(0.6),
                marginRight: theme.spacing(0.5),
                paddingLeft: theme.spacing(2.5),
              }}
            >
              {invalidLocations.map((l, i) => (
                <Typography
                  component={'li'}
                  variant="subtitle2"
                  key={i}
                  marginTop={0.8}
                >
                  {`${l.billingLocation ? 'Bill-to' : 'Service'} Location: `}
                  <Typography
                    variant="subtitle2"
                    component={'span'}
                    sx={{
                      cursor: 'pointer',
                      color: '#1565c0',
                    }}
                    onClick={() =>
                      scrollToLocation(`locations.${l.id}.address1`)
                    }
                  >
                    {getlocationInfo(l.id)}
                  </Typography>
                </Typography>
              ))}
            </ul>
          </Box>
        </Box>
      </Paper>
    </Stack>
  )
}
CreateLocationHeader.displayName = 'CreateLocationHeader'
